import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Building2, Plus, Edit, Trash2, Clock, Globe,
    ExternalLink, ChevronRight, Loader2, Calendar, LayoutGrid, List,
    Headphones, Megaphone, Server, Users, CheckSquare, Square, Pencil, X, Save, Search, Filter, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${BACKEND_URL}/api`;

// ── Client Type Config ────────────────────────────────────────────────────

const CLIENT_TYPE_CONFIG = {
    support: {
        label: 'Support',
        icon: Headphones,
        badge: 'bg-purple-100 text-purple-700 border-purple-200',
        card: 'bg-purple-50/60 border-purple-200',
        header: 'bg-purple-50 border-b border-purple-100',
        dot: 'bg-purple-500',
    },
    marketing: {
        label: 'Marketing',
        icon: Megaphone,
        badge: 'bg-blue-100 text-blue-700 border-blue-200',
        card: 'bg-blue-50/60 border-blue-200',
        header: 'bg-blue-50 border-b border-blue-100',
        dot: 'bg-blue-500',
    },
    hosting: {
        label: 'Hosting',
        icon: Server,
        badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        card: 'bg-emerald-50/60 border-emerald-200',
        header: 'bg-emerald-50 border-b border-emerald-100',
        dot: 'bg-emerald-500',
    },
};

const ALL_TYPES = ['support', 'marketing', 'hosting'];
const HOSTING_OPTIONS = ['WHC 1', 'WHC 2', 'SG 1', 'SG 2', 'SG 3', 'Digital Ocean', 'AWS EC2', 'Avelica Server (Hostgator)'];

const HOSTING_COLORS = {
    'WHC 1':         'bg-sky-100 text-sky-700 border-sky-200',
    'WHC 2':         'bg-sky-100 text-sky-700 border-sky-200',
    'SG 1':          'bg-orange-100 text-orange-700 border-orange-200',
    'SG 2':          'bg-orange-100 text-orange-700 border-orange-200',
    'SG 3':          'bg-orange-100 text-orange-700 border-orange-200',
    'Digital Ocean': 'bg-blue-100 text-blue-700 border-blue-200',
    'AWS EC2':       'bg-amber-100 text-amber-700 border-amber-200',
    'Avelica Server (Hostgator)': 'bg-teal-100 text-teal-700 border-teal-200',
};

// Pick the "dominant" type for card coloring (first match)
const getDominantType = (clientTypes = [], isSupportClient = false) => {
    if (clientTypes.includes('support') || isSupportClient) return 'support';
    if (clientTypes.includes('marketing')) return 'marketing';
    if (clientTypes.includes('hosting')) return 'hosting';
    return null;
};

// Checkbox row for type selection
const TypeCheckbox = ({ type, checked, onChange }) => {
    const cfg = CLIENT_TYPE_CONFIG[type];
    const Icon = cfg.icon;
    return (
        <button
            type="button"
            onClick={() => onChange(type)}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-all text-left w-full ${checked
                ? `${cfg.badge} border font-semibold`
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
        >
            {checked
                ? <CheckSquare className="w-4 h-4 shrink-0" />
                : <Square className="w-4 h-4 shrink-0 text-gray-400" />
            }
            <Icon className="w-4 h-4 shrink-0" />
            <span className="text-sm">{cfg.label} Client</span>
        </button>
    );
};

// ── Component ─────────────────────────────────────────────────────────────

const Companies = ({ user }) => {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [hoursByCompany, setHoursByCompany] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [editCompany, setEditCompany] = useState(null);
    const [form, setForm] = useState({ name: '', url: '', notes: '', client_types: [], hosting: '', exclude_from_maintenance: false });
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [showBulkEdit, setShowBulkEdit] = useState(false);
    const [bulkEditForm, setBulkEditForm] = useState({ client_types: [], hosting: '' });
    const [bulkUpdating, setBulkUpdating] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // company to delete
    const [deleteTicketCount, setDeleteTicketCount] = useState(0);
    const [reassignTo, setReassignTo] = useState('none');
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [dateRangeType, setDateRangeType] = useState('all');
    const [customRange, setCustomRange] = useState({ start: '', end: '' });
    const [viewMode, setViewMode] = useState('grid');
    const [sortBy, setSortBy] = useState('company');
    const [typeFilters, setTypeFilters] = useState(new Set()); // empty = all
    const [searchQuery, setSearchQuery] = useState('');
    const [subTagFilter, setSubTagFilter] = useState(''); // e.g. 'WHC 1', 'GoDaddy'
    const [infraOptions, setInfraOptions] = useState({ hosting_locations: [], domain_registrars: [], dns_zone_editors: [] });

    const getAuthHeaders = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            let hoursUrl = `${API}/admin/hours-by-company`;
            if (dateRangeType !== 'all') {
                const now = new Date();
                let start = new Date(); let end = new Date();
                if (dateRangeType === 'today') {
                    start.setHours(0, 0, 0, 0); end.setHours(23, 59, 59, 999);
                } else if (dateRangeType === 'yesterday') {
                    start.setDate(now.getDate() - 1); start.setHours(0, 0, 0, 0); end.setDate(now.getDate() - 1); end.setHours(23, 59, 59, 999);
                } else if (dateRangeType === 'this_week') {
                    const f = now.getDate() - now.getDay(); start = new Date(now.setDate(f)); start.setHours(0, 0, 0, 0); end = new Date();
                } else if (dateRangeType === 'this_month') {
                    start = new Date(now.getFullYear(), now.getMonth(), 1); end = new Date();
                } else if (dateRangeType === 'custom' && customRange.start && customRange.end) {
                    start = new Date(customRange.start + 'T00:00:00'); end = new Date(customRange.end + 'T23:59:59');
                }
                if (dateRangeType !== 'custom' || (customRange.start && customRange.end)) {
                    hoursUrl += `?start_date=${start.toISOString()}&end_date=${end.toISOString()}`;
                }
            }
            const [companiesRes, hoursRes] = await Promise.all([
                axios.get(`${API}/companies`, getAuthHeaders()),
                axios.get(hoursUrl, getAuthHeaders()),
            ]);
            setCompanies(companiesRes.data);
            setHoursByCompany(hoursRes.data);
        } catch { toast.error('Failed to load companies'); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        if (dateRangeType !== 'custom' || (customRange.start && customRange.end)) fetchData();
    }, [dateRangeType, customRange.start, customRange.end]); // eslint-disable-line

    useEffect(() => {
        axios.get(`${API}/companies/infra-options`, getAuthHeaders())
            .then(r => setInfraOptions(r.data))
            .catch(() => {});
    }, []); // eslint-disable-line

    const resetForm = () => setForm({ name: '', url: '', notes: '', client_types: [], hosting: '', exclude_from_maintenance: false });

    const toggleType = (type) => {
        setForm(f => ({
            ...f,
            client_types: f.client_types.includes(type)
                ? f.client_types.filter(t => t !== type)
                : [...f.client_types, type],
        }));
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error('Company name is required'); return; }
        try {
            await axios.post(`${API}/companies`, form, getAuthHeaders());
            toast.success('Company created');
            setCreateOpen(false); resetForm(); fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create company');
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        try {
            await axios.patch(`${API}/companies/${editCompany.id}`, form, getAuthHeaders());
            toast.success('Company updated');
            setEditCompany(null); resetForm(); fetchData();
        } catch { toast.error('Failed to update company'); }
    };

    const handleDelete = async (company) => {
        // Check how many tickets are linked
        try {
            const res = await axios.get(`${API}/companies/${company.id}/ticket-count`, getAuthHeaders());
            const count = res.data.count || 0;
            setDeleteTicketCount(count);
            setDeleteTarget(company);
            setReassignTo('none');
            if (count === 0) {
                // No tickets — just confirm
                if (window.confirm(`Delete "${company.name}"? No tickets are linked to this company.`)) {
                    await axios.delete(`${API}/companies/${company.id}`, getAuthHeaders());
                    setSelectedIds(prev => { const n = new Set(prev); n.delete(company.id); return n; });
                    setDeleteTarget(null);
                    toast.success('Company deleted');
                    fetchData();
                } else {
                    setDeleteTarget(null);
                }
            }
            // If count > 0, the dialog will open via deleteTarget state
        } catch { toast.error('Failed to check company tickets'); }
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await axios.post(
                `${API}/companies/${deleteTarget.id}/delete-reassign`,
                { reassign_to: reassignTo === 'none' ? null : reassignTo },
                getAuthHeaders()
            );
            setSelectedIds(prev => { const n = new Set(prev); n.delete(deleteTarget.id); return n; });
            toast.success(`"${deleteTarget.name}" deleted${reassignTo !== 'none' ? ' — tickets reassigned' : ''}`);
            setDeleteTarget(null);
            fetchData();
        } catch { toast.error('Failed to delete company'); }
        finally { setDeleteLoading(false); }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Delete ${selectedIds.size} selected companies? Their tickets won't be deleted.`)) return;
        setBulkDeleting(true);
        try {
            await axios.post(`${API}/companies/bulk-delete`, { ids: Array.from(selectedIds) }, getAuthHeaders());
            toast.success(`${selectedIds.size} companies deleted`);
            setSelectedIds(new Set());
            fetchData();
        } catch { toast.error('Failed to delete companies'); }
        finally { setBulkDeleting(false); }
    };

    const handleBulkUpdate = async () => {
        if (selectedIds.size === 0) return;
        setBulkUpdating(true);
        try {
            const payload = { ids: Array.from(selectedIds) };
            payload.client_types = bulkEditForm.client_types;
            payload.hosting = bulkEditForm.hosting;
            await axios.post(`${API}/companies/bulk-update`, payload, getAuthHeaders());
            toast.success(`${selectedIds.size} companies updated`);
            setShowBulkEdit(false);
            setSelectedIds(new Set());
            fetchData();
        } catch { toast.error('Failed to update companies'); }
        finally { setBulkUpdating(false); }
    };

    const toggleBulkEditType = (type) => {
        setBulkEditForm(f => ({
            ...f,
            client_types: f.client_types.includes(type)
                ? f.client_types.filter(t => t !== type)
                : [...f.client_types, type],
        }));
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === displayCompanies.length) setSelectedIds(new Set());
        else setSelectedIds(new Set(displayCompanies.map(c => c.id)));
    };

    const openEdit = (company) => {
        setEditCompany(company);
        setForm({
            name: company.name,
            url: company.url || '',
            notes: company.notes || '',
            client_types: company.client_types || [],
            hosting: company.hosting || '',
            exclude_from_maintenance: Boolean(company.exclude_from_maintenance),
        });
    };

    // ── Derived ────────────────────────────────────────────────────────────

    const hoursMap = {};
    hoursByCompany.forEach(h => { if (h.company.id) hoursMap[h.company.id] = h; });

    // Build displayCompanies FIRST so all stats react to active filters
    const displayCompanies = (dateRangeType !== 'all'
        ? companies.filter(c => { const h = hoursMap[c.id] || {}; return (h.ticket_count || 0) > 0 || (h.total_hours || 0) > 0; })
        : companies
    )
        .filter(c => {
            // Name + domain search
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase().trim();
                const nameMatch = c.name.toLowerCase().includes(q);
                const urlMatch = (c.url || '').toLowerCase().includes(q);
                if (!nameMatch && !urlMatch) return false;
            }
            // Type filter: empty set = show all
            if (typeFilters.size > 0) {
                const types = c.client_types || [];
                let match = false;
                if (typeFilters.has('support') && (types.includes('support') || c.is_support_client)) match = true;
                if (typeFilters.has('marketing') && types.includes('marketing')) match = true;
                if (typeFilters.has('hosting') && types.includes('hosting')) match = true;
                if (!match) return false;
            }
            // Sub-tag filter
            if (subTagFilter) {
                const h = (c.hosting || '').toLowerCase();
                const hl = (c.hosting_location || '').toLowerCase();
                const dr = (c.domain_registrar || '').toLowerCase();
                const dz = (c.dns_zone_editor || '').toLowerCase();
                const tag = subTagFilter.toLowerCase();
                if (h !== tag && hl !== tag && dr !== tag && dz !== tag) return false;
            }
            return true;
        })
        .sort((a, b) => {
            const hA = hoursMap[a.id] || {}; const hB = hoursMap[b.id] || {};
            if (sortBy === 'tickets') return (hB.ticket_count || 0) - (hA.ticket_count || 0);
            if (sortBy === 'hours') return (hB.total_hours || 0) - (hA.total_hours || 0);
            return a.name.localeCompare(b.name);
        });

    // Stats derived from displayCompanies — updates with every filter change
    const totalHours = displayCompanies.reduce((s, c) => s + (hoursMap[c.id]?.total_hours || 0), 0);
    const totalTickets = displayCompanies.reduce((s, c) => s + (hoursMap[c.id]?.ticket_count || 0), 0);
    const supportCount = displayCompanies.filter(c => (c.client_types || []).includes('support') || c.is_support_client).length;
    const marketingCount = displayCompanies.filter(c => (c.client_types || []).includes('marketing')).length;
    const hostingCount = displayCompanies.filter(c => (c.client_types || []).includes('hosting')).length;


    // ── Form section (shared for create + edit) ────────────────────────────

    const renderCompanyForm = (onSubmit, submitLabel) => (
        <form onSubmit={onSubmit} className="space-y-4 pt-2">
            <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Acme Corp" required />
            </div>
            <div className="space-y-2">
                <Label>Website URL</Label>
                <Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://acme.com" />
            </div>
            <div className="space-y-2">
                <Label>Client Types <span className="text-gray-400 font-normal text-xs">(select all that apply)</span></Label>
                <div className="grid grid-cols-1 gap-2">
                    {ALL_TYPES.map(type => (
                        <TypeCheckbox key={type} type={type} checked={form.client_types.includes(type)} onChange={toggleType} />
                    ))}
                </div>
            </div>
            {form.client_types.includes('hosting') && (
                <div className="space-y-2">
                    <Label>Hosting Provider</Label>
                    <Select value={form.hosting || 'none'} onValueChange={v => setForm({ ...form, hosting: v === 'none' ? '' : v })}>
                        <SelectTrigger><SelectValue placeholder="Select hosting provider..." /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {HOSTING_OPTIONS.map(h => (
                                <SelectItem key={h} value={h}>
                                    <span className="flex items-center gap-2">
                                        <span className={`w-2 h-2 rounded-full ${(HOSTING_COLORS[h] || '').split(' ')[0]}`} />
                                        {h}
                                    </span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}
            <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes..." rows={2} />
            </div>
            <label className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 cursor-pointer">
                <Checkbox
                    checked={form.exclude_from_maintenance}
                    onCheckedChange={checked => setForm({ ...form, exclude_from_maintenance: checked === true })}
                    className="mt-0.5"
                />
                <span>
                    <span className="block text-sm font-semibold text-amber-900">Exclude from website maintenance</span>
                    <span className="block text-xs text-amber-700 mt-0.5">Do not create recurring monthly maintenance tasks for this company.</span>
                </span>
            </label>
            <Button type="submit" className="w-full bg-black hover:bg-gray-800">{submitLabel}</Button>
        </form>
    );

    // ── Render ─────────────────────────────────────────────────────────────

    return (
        <div className="min-h-screen bg-gray-50">
            <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <Button variant="outline" size="sm" onClick={() => navigate('/admin')} className="gap-2">
                                <ArrowLeft className="w-4 h-4" /> Admin
                            </Button>
                            <div className="h-6 w-px bg-gray-200 mx-1" />
                            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <Building2 className="w-5 h-5" /> Companies
                            </h1>
                        </div>
                        <Dialog open={createOpen} onOpenChange={v => { setCreateOpen(v); if (!v) resetForm(); }}>
                            <DialogTrigger asChild>
                                <Button className="bg-black hover:bg-gray-800 gap-2">
                                    <Plus className="w-4 h-4" /> New Company
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                                <DialogHeader><DialogTitle>Create Company Profile</DialogTitle></DialogHeader>
                                {renderCompanyForm(handleCreate, 'Create Company')}
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Row — all 6 equal-width columns */}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
                    {[
                        { label: 'Companies', value: displayCompanies.length, icon: Building2, cls: 'bg-white' },
                        { label: 'Hours', value: `${totalHours.toFixed(1)}h`, icon: Clock, cls: 'bg-orange-50 border-orange-200' },
                        { label: 'Tickets', value: totalTickets, icon: Globe, cls: 'bg-blue-50 border-blue-200' },
                        { label: 'Support', value: supportCount, icon: Headphones, cls: 'bg-purple-50 border-purple-200' },
                        { label: 'Marketing', value: marketingCount, icon: Megaphone, cls: 'bg-blue-50 border-blue-200' },
                        { label: 'Hosting', value: hostingCount, icon: Server, cls: 'bg-emerald-50 border-emerald-200' },
                    ].map(({ label, value, icon: Icon, cls }) => (
                        <Card key={label} className={`border ${cls}`}>
                            <CardContent className="p-3 flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-gray-500 font-medium leading-tight">{label}</p>
                                    <p className="text-lg font-bold mt-0.5 leading-tight">{value}</p>
                                </div>
                                <Icon className="w-4 h-4 text-gray-300 shrink-0" />
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Search bar */}
                <div className="mb-4">
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Search by name or domain..."
                            className="pl-9 h-10 bg-white border shadow-sm"
                        />
                        {searchQuery && (
                            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Filters bar */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
                    {/* Left: date + type filter */}
                    <div className="flex flex-wrap gap-2 items-center">
                        <div className="flex gap-2 items-center bg-white p-2 rounded-lg border shadow-sm">
                            <Calendar className="w-4 h-4 text-gray-500 ml-1" />
                            <Select value={dateRangeType} onValueChange={setDateRangeType}>
                                <SelectTrigger className="w-[160px] border-none focus:ring-0 shadow-none h-8">
                                    <SelectValue placeholder="Timeframe" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Time</SelectItem>
                                    <SelectItem value="today">Today</SelectItem>
                                    <SelectItem value="yesterday">Yesterday</SelectItem>
                                    <SelectItem value="this_week">This Week</SelectItem>
                                    <SelectItem value="this_month">This Month</SelectItem>
                                    <SelectItem value="custom">Custom Range</SelectItem>
                                </SelectContent>
                            </Select>
                            {dateRangeType === 'custom' && (
                                <div className="flex items-center gap-2 pl-2 border-l ml-1">
                                    <Input type="date" className="w-auto h-8 text-xs" value={customRange.start} onChange={e => setCustomRange({ ...customRange, start: e.target.value })} />
                                    <span className="text-xs text-gray-400">to</span>
                                    <Input type="date" className="w-auto h-8 text-xs" value={customRange.end} onChange={e => setCustomRange({ ...customRange, end: e.target.value })} />
                                </div>
                            )}
                        </div>


                        {/* Type filter pills — toggle each independently */}
                        <div className="flex items-center gap-1.5 bg-white p-2 rounded-lg border shadow-sm flex-wrap">
                            <button
                                onClick={() => { setTypeFilters(new Set()); setSubTagFilter(''); }}
                                className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${typeFilters.size === 0 ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'
                                    }`}
                            >
                                All
                            </button>
                            {[['support', 'Support', 'bg-purple-100 text-purple-700 border border-purple-300'],
                            ['marketing', 'Marketing', 'bg-blue-100 text-blue-700 border border-blue-300'],
                            ['hosting', 'Hosting', 'bg-emerald-100 text-emerald-700 border border-emerald-300']
                            ].map(([val, label, activeCls]) => {
                                const active = typeFilters.has(val);
                                return (
                                    <button key={val}
                                        onClick={() => {
                                            setTypeFilters(prev => {
                                                const next = new Set(prev);
                                                if (next.has(val)) next.delete(val); else next.add(val);
                                                return next;
                                            });
                                            setSubTagFilter('');
                                        }}
                                        className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${active ? activeCls : 'text-gray-500 hover:bg-gray-100'
                                            }`}
                                    >
                                        {active ? '✓ ' : ''}{label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Right: sort + view toggle */}
                    <div className="flex gap-2 items-center">
                        <Select value={sortBy} onValueChange={setSortBy}>
                            <SelectTrigger className="w-[160px] h-9 bg-white border">
                                <SelectValue placeholder="Sort By..." />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="company">Sort by Name</SelectItem>
                                <SelectItem value="tickets">Sort by Tickets</SelectItem>
                                <SelectItem value="hours">Sort by Hours</SelectItem>
                            </SelectContent>
                        </Select>
                        <div className="flex items-center bg-white border rounded-md p-1">
                            <Button variant={viewMode === 'grid' ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2" onClick={() => setViewMode('grid')}>
                                <LayoutGrid className="w-4 h-4" />
                            </Button>
                            <Button variant={viewMode === 'list' ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2" onClick={() => setViewMode('list')}>
                                <List className="w-4 h-4" />
                            </Button>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-9 px-3 text-xs gap-1.5"
                            onClick={() => {
                                const rows = [['Company', 'Domain', 'Types', 'Hosting', 'Hosting Location', 'Domain Registrar', 'DNS Zone Editor', 'Tickets', 'Hours']];
                                displayCompanies.forEach(c => {
                                    const h = hoursMap[c.id] || {};
                                    rows.push([
                                        c.name,
                                        c.url || '',
                                        (c.client_types || []).join(', '),
                                        c.hosting || '',
                                        c.hosting_location || '',
                                        c.domain_registrar || '',
                                        c.dns_zone_editor || '',
                                        h.ticket_count || 0,
                                        (h.total_hours || 0).toFixed(1),
                                    ]);
                                });
                                const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
                                const blob = new Blob([csv], { type: 'text/csv' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url; a.download = `companies_${new Date().toISOString().split('T')[0]}.csv`;
                                a.click(); URL.revokeObjectURL(url);
                                toast.success(`Exported ${displayCompanies.length} companies`);
                            }}
                        >
                            <Download className="w-3.5 h-3.5" />Export
                        </Button>
                    </div>
                </div>

                {/* Sub-tag filter pills */}
                {(() => {
                    const allSubTags = [];
                    const hostingTags = [...new Set([...HOSTING_OPTIONS, ...(infraOptions.hosting_locations || [])])];
                    hostingTags.forEach(t => allSubTags.push({ value: t, label: t, color: HOSTING_COLORS[t] || 'bg-emerald-100 text-emerald-700 border-emerald-300', category: 'Hosting' }));
                    (infraOptions.domain_registrars || []).forEach(t => allSubTags.push({ value: t, label: t, color: 'bg-blue-100 text-blue-700 border-blue-300', category: 'Registrar' }));
                    (infraOptions.dns_zone_editors || []).forEach(t => allSubTags.push({ value: t, label: t, color: 'bg-violet-100 text-violet-700 border-violet-300', category: 'DNS' }));

                    if (allSubTags.length === 0) return null;
                    return (
                        <div className="flex items-center gap-1.5 flex-wrap bg-white/70 p-2.5 rounded-lg border border-dashed border-gray-200 mb-4">
                            <Filter className="w-3.5 h-3.5 text-gray-400 ml-1 shrink-0" />
                            <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider mr-1">Filter by</span>
                            {subTagFilter && (
                                <button onClick={() => setSubTagFilter('')} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-900 text-white font-medium mr-1">✕ Clear</button>
                            )}
                            {allSubTags.map(tag => (
                                <button
                                    key={`${tag.category}-${tag.value}`}
                                    onClick={() => setSubTagFilter(subTagFilter === tag.value ? '' : tag.value)}
                                    className={`text-[11px] px-2 py-0.5 rounded-full font-medium border transition-all ${subTagFilter === tag.value ? tag.color + ' ring-1 ring-offset-1 shadow-sm' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                                >
                                    {tag.label}
                                </button>
                            ))}
                        </div>
                    );
                })()}

                {/* Bulk Select / Action Bar */}
                {!loading && displayCompanies.length > 0 && (
                    <div className={`mb-3 bg-white rounded-lg border shadow-sm overflow-hidden transition-all ${selectedIds.size > 0 ? 'border-indigo-300 ring-1 ring-indigo-100' : ''}`}>
                        <div className="flex items-center gap-3 px-4 py-2">
                            <input type="checkbox" checked={selectedIds.size === displayCompanies.length && displayCompanies.length > 0} onChange={toggleSelectAll} className="w-4 h-4 accent-indigo-600 cursor-pointer rounded" />
                            <span className="text-xs text-gray-500 flex-1">
                                {selectedIds.size > 0 ? `${selectedIds.size} of ${displayCompanies.length} selected` : `Select all ${displayCompanies.length} companies`}
                            </span>
                            {selectedIds.size > 0 && (
                                <div className="flex items-center gap-1.5">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => { setShowBulkEdit(v => !v); setBulkEditForm({ client_types: [], hosting: '' }); }}
                                        className={`h-7 px-2 text-xs gap-1 ${showBulkEdit ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : ''}`}
                                    >
                                        {showBulkEdit ? <X className="w-3 h-3" /> : <Pencil className="w-3 h-3" />}
                                        {showBulkEdit ? 'Cancel' : 'Edit'}
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={handleBulkDelete}
                                        disabled={bulkDeleting}
                                        className="h-7 px-2 text-xs gap-1"
                                    >
                                        {bulkDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                        Delete
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Bulk Edit Panel */}
                        {showBulkEdit && selectedIds.size > 0 && (
                            <div className="px-4 py-3 border-t border-indigo-100 bg-indigo-50/50 space-y-3">
                                <p className="text-xs font-medium text-indigo-700">Apply to {selectedIds.size} selected companies:</p>

                                <div>
                                    <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Client Types</p>
                                    <div className="flex flex-wrap gap-2">
                                        {ALL_TYPES.map(type => {
                                            const cfg = CLIENT_TYPE_CONFIG[type];
                                            const Icon = cfg.icon;
                                            const checked = bulkEditForm.client_types.includes(type);
                                            return (
                                                <button
                                                    key={type}
                                                    type="button"
                                                    onClick={() => toggleBulkEditType(type)}
                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                                                        checked ? `${cfg.badge} font-semibold` : 'border-gray-200 text-gray-500 bg-white hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {checked ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                                                    <Icon className="w-3.5 h-3.5" />
                                                    {cfg.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {bulkEditForm.client_types.includes('hosting') && (
                                    <div>
                                        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Hosting Provider</p>
                                        <Select value={bulkEditForm.hosting || 'none'} onValueChange={v => setBulkEditForm({ ...bulkEditForm, hosting: v === 'none' ? '' : v })}>
                                            <SelectTrigger className="h-8 text-xs w-48 bg-white">
                                                <SelectValue placeholder="Select provider..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">None</SelectItem>
                                                {HOSTING_OPTIONS.map(h => (
                                                    <SelectItem key={h} value={h}>
                                                        <span className="flex items-center gap-2">
                                                            <span className={`w-2 h-2 rounded-full ${(HOSTING_COLORS[h] || '').split(' ')[0]}`} />
                                                            {h}
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                <Button
                                    size="sm"
                                    onClick={handleBulkUpdate}
                                    disabled={bulkUpdating}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 px-4 text-xs gap-1.5"
                                >
                                    {bulkUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                    Apply to {selectedIds.size} companies
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/* Company Cards */}
                {loading ? (
                    <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
                ) : displayCompanies.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
                        <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-700">No Companies Found</h3>
                        <p className="text-gray-500 mt-2 text-sm">No companies match the selected filters.</p>
                    </div>
                ) : (
                    <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5' : 'space-y-3'}>
                        {displayCompanies.map(company => {
                            const hData = hoursMap[company.id] || { total_hours: 0, ticket_count: 0 };
                            const clientTypes = company.client_types || [];
                            const dominant = getDominantType(clientTypes, company.is_support_client);
                            const cfg = dominant ? CLIENT_TYPE_CONFIG[dominant] : null;

                            // Effective types to show (merge is_support_client)
                            const effectiveTypes = [...new Set([
                                ...(company.is_support_client && !clientTypes.includes('support') ? ['support'] : []),
                                ...clientTypes,
                            ])];

                            return viewMode === 'list' ? (
                                <Card key={company.id}
                                    className={`hover:shadow-md transition-shadow overflow-hidden ${selectedIds.has(company.id) ? 'ring-2 ring-indigo-400' : ''} ${cfg ? `border ${cfg.card}` : 'bg-white'}`}>
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 px-5 gap-3">
                                        <input type="checkbox" checked={selectedIds.has(company.id)} onChange={() => toggleSelect(company.id)} className="w-4 h-4 accent-indigo-600 cursor-pointer rounded shrink-0 mt-1 sm:mt-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="text-base font-bold text-gray-900 truncate">{company.name}</h3>
                                                {effectiveTypes.map(t => {
                                                    const c = CLIENT_TYPE_CONFIG[t];
                                                    if (!c) return null;
                                                    const TIcon = c.icon;
                                                    return (
                                                        <span key={t} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${c.badge}`}>
                                                            <TIcon className="w-3 h-3" />{c.label}
                                                        </span>
                                                    );
                                                })}
                                                {company.hosting && (
                                                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${HOSTING_COLORS[company.hosting] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                                                        <Server className="w-3 h-3" />{company.hosting}
                                                    </span>
                                                )}
                                                {company.exclude_from_maintenance && (
                                                    <Badge className="bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100">Maintenance excluded</Badge>
                                                )}
                                            </div>
                                            {company.url && (
                                                <a href={company.url.startsWith('http') ? company.url : `https://${company.url}`}
                                                    target="_blank" rel="noopener noreferrer"
                                                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5"
                                                    onClick={e => e.stopPropagation()}>
                                                    <ExternalLink className="w-3 h-3" />{company.url}
                                                </a>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-5 shrink-0">
                                            <div className="text-right"><p className="text-xs text-gray-500">Tickets</p><p className="font-bold text-blue-700">{hData.ticket_count}</p></div>
                                            <div className="text-right"><p className="text-xs text-gray-500">Hours</p><p className="font-bold text-orange-700">{hData.total_hours.toFixed(1)}h</p></div>
                                            <div className="flex items-center gap-1.5">
                                                <Button variant="outline" size="sm" className="h-8" onClick={() => navigate(`/admin/companies/${company.id}`)}>Details</Button>
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(company)}><Edit className="w-4 h-4 text-gray-500" /></Button>
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-red-50" onClick={() => handleDelete(company)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            ) : (
                                <Card key={company.id}
                                    className={`hover:shadow-md transition-all group overflow-hidden ${selectedIds.has(company.id) ? 'ring-2 ring-indigo-400' : ''} ${cfg ? `border-2 ${cfg.card}` : 'bg-white border'}`}>
                                    <CardHeader className={`pb-3 ${cfg ? cfg.header : 'bg-gray-50/50 border-b border-gray-100'}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <input type="checkbox" checked={selectedIds.has(company.id)} onChange={() => toggleSelect(company.id)} className="w-4 h-4 accent-indigo-600 cursor-pointer rounded shrink-0 mt-1" />
                                            <div className="flex-1 min-w-0">
                                                <CardTitle className="text-base truncate">{company.name}</CardTitle>
                                                {company.url && (
                                                    <a href={company.url.startsWith('http') ? company.url : `https://${company.url}`}
                                                        target="_blank" rel="noopener noreferrer"
                                                        className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5"
                                                        onClick={e => e.stopPropagation()}>
                                                        <ExternalLink className="w-3 h-3" />{company.url}
                                                    </a>
                                                )}
                                                {/* Type badges */}
                                                {effectiveTypes.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                                        {effectiveTypes.map(t => {
                                                            const c = CLIENT_TYPE_CONFIG[t];
                                                            if (!c) return null;
                                                            const TIcon = c.icon;
                                                            return (
                                                                <span key={t} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${c.badge}`}>
                                                                    <TIcon className="w-3 h-3" />{c.label}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                {company.hosting && (
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${HOSTING_COLORS[company.hosting] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                                                            <Server className="w-3 h-3" />{company.hosting}
                                                        </span>
                                                    </div>
                                                )}
                                                {company.exclude_from_maintenance && (
                                                    <Badge className="mt-1 bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100">Maintenance excluded</Badge>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(company)}>
                                                    <Edit className="w-3.5 h-3.5 text-gray-500" />
                                                </Button>
                                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-red-50" onClick={() => handleDelete(company)}>
                                                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                                </Button>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="pt-4">
                                        <div className="flex items-center gap-3 mb-3">
                                            <Badge variant="outline" className="gap-1.5 text-orange-700 border-orange-200 bg-orange-50 font-medium py-1">
                                                <Clock className="w-3.5 h-3.5" />{hData.total_hours.toFixed(1)}h
                                            </Badge>
                                            <Badge variant="outline" className="gap-1.5 text-blue-700 border-blue-200 bg-blue-50 font-medium py-1">
                                                <Globe className="w-3.5 h-3.5" />{hData.ticket_count} tickets
                                            </Badge>
                                        </div>
                                        {company.notes && (
                                            <p className="text-xs text-gray-500 italic line-clamp-2 mb-3 bg-gray-50 rounded px-2 py-1.5">{company.notes}</p>
                                        )}
                                        <Button variant="outline" size="sm" className="w-full gap-2"
                                            onClick={() => navigate(`/admin/companies/${company.id}`)}>
                                            View Details <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </main>

            {/* Edit Dialog */}
            <Dialog open={!!editCompany} onOpenChange={v => { if (!v) { setEditCompany(null); resetForm(); } }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader><DialogTitle>Edit {editCompany?.name}</DialogTitle></DialogHeader>
                    {renderCompanyForm(handleEdit, 'Save Changes')}
                </DialogContent>
            </Dialog>

            {/* Delete + Reassign Dialog */}
            <Dialog open={!!deleteTarget && deleteTicketCount > 0} onOpenChange={v => { if (!v) setDeleteTarget(null); }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-700">
                            <Trash2 className="w-5 h-5" /> Delete "{deleteTarget?.name}"
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-1">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <p className="text-sm text-amber-800 font-medium">
                                ⚠️ This company has <span className="font-bold">{deleteTicketCount}</span> ticket{deleteTicketCount !== 1 ? 's' : ''} linked to it.
                            </p>
                            <p className="text-xs text-amber-600 mt-1">Choose a company to reassign those tickets to, or leave blank to unlink them.</p>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Reassign tickets to:</Label>
                            <Select value={reassignTo} onValueChange={setReassignTo}>
                                <SelectTrigger><SelectValue placeholder="Select a company..." /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">
                                        <span className="text-gray-500 italic">Don't reassign — unlink tickets</span>
                                    </SelectItem>
                                    {companies
                                        .filter(c => c.id !== deleteTarget?.id)
                                        .map(c => (
                                            <SelectItem key={c.id} value={c.id}>
                                                <span className="flex items-center gap-2">
                                                    <Building2 className="w-3 h-3 text-gray-400" />
                                                    {c.name}
                                                </span>
                                            </SelectItem>
                                        ))
                                    }
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={handleConfirmDelete}
                                disabled={deleteLoading}
                                className="gap-1.5"
                            >
                                {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                Delete & {reassignTo !== 'none' ? 'Reassign' : 'Unlink'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Companies;
